import {createBrowserRouter, Navigate} from "react-router-dom";
import React, {Suspense, lazy, ReactNode} from "react";
import SuspenseLoader from "./components/feedBack/SuspenseLoader";

// ─── Layouts (statiques car légers et structurels) ───
import DefaultLayout from "./components/layouts/DefaultLayout";
import GuestLayout from "./components/layouts/GuestLayout";
import PublicLayout from "./components/layouts/PublicLayout";
import EspaceLayout from "./components/layouts/EspaceLayout";
import RequireStaff from "./components/router/RequireStaff";
import RequireHotelier from "./components/router/RequireHotelier";
import RequirePermission from "./components/router/RequirePermission";
import SmartRedirect from "./components/router/SmartRedirect";
import {authLoader} from "./components/router/authLoader";
import NotFound from "./pages/NotFound";

// ─── Lazy import avec retry automatique sur erreur de chunk ───
// Après un déploiement, le navigateur peut avoir d'anciens chunks en cache
// qui référencent des sous-chunks qui n'existent plus. On recharge la page une seule fois.
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
    return lazy(() =>
        factory().catch(() => {
            const key = 'chunk_reload_' + window.location.pathname;
            if (!sessionStorage.getItem(key)) {
                sessionStorage.setItem(key, '1');
                window.location.reload();
            }
            return factory();
        })
    );
}

// ─── Suspense wrapper ───
function S({ children }: { children: ReactNode }) {
    return <Suspense fallback={<SuspenseLoader />}>{children}</Suspense>;
}

// ─── Lazy imports : Pages publiques ───
const SurveyResponses = lazyWithRetry(() => import("./pages/surveys-responses/SurveyResponses"));
const PublicDocument = lazyWithRetry(() => import("./pages/public-document/PublicDocument"));
const ReportExportView = lazyWithRetry(() => import("./pages/reports/ReportExportView"));

// ─── Lazy imports : Auth ───
const Login = lazyWithRetry(() => import("./pages/auth/Login"));
const Signup = lazyWithRetry(() => import("./pages/auth/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/auth/ResetPassword"));
const TwoFactorSetup = lazyWithRetry(() => import("./pages/auth/TwoFactorSetup"));
const AcceptInvitation = lazyWithRetry(() => import("./pages/auth/AcceptInvitation"));

// Account
const AccountSecurity = lazyWithRetry(() => import("./pages/account/Security"));

// Dashboard
const DashboardIndex = lazyWithRetry(() => import("./pages/dashboard/Index"));

// Campaigns
const CampaignsIndex = lazyWithRetry(() => import("./pages/campaigns/Index"));
const CampaignsShow = lazyWithRetry(() => import("./pages/campaigns/Show"));
const CampaignsAlerts = lazyWithRetry(() => import("./pages/campaigns/Alerts"));
const CampaignsWorkbench = lazyWithRetry(() => import("./pages/campaigns/EditorialWorkbench"));
const CampaignsEdit = lazyWithRetry(() => import("./pages/campaigns/Edit"));
const CampaignsEditFlows = lazyWithRetry(() => import("./pages/campaigns/EditFlows"));

// Alerts
const AlertsIndex = lazyWithRetry(() => import("./pages/alerts/Index"));

// Flows
const FlowsIndex = lazyWithRetry(() => import("./pages/flows/Index"));
const FlowsUpdate = lazyWithRetry(() => import("./pages/flows/Update"));
const FlowsCreate = lazyWithRetry(() => import("./pages/flows/Create"));
const FlowStatistics = lazyWithRetry(() => import("./pages/flows/statistics/Statistics"));
const FlowAnalyse = lazyWithRetry(() => import("./pages/flows/statistics/Analyse"));
const FlowFlow = lazyWithRetry(() => import("./pages/flows/statistics/Flow"));
const FlowLogs = lazyWithRetry(() => import("./pages/flows/statistics/Logs"));
const FlowHeatmap = lazyWithRetry(() => import("./pages/flows/statistics/Heatmap"));

// Surveys (admin)
const SurveysIndex = lazyWithRetry(() => import("./pages/surveys/Index"));
const SurveysCreate = lazyWithRetry(() => import("./pages/surveys/Create"));
const SurveysUpdate = lazyWithRetry(() => import("./pages/surveys/Update"));
const SurveysResponses = lazyWithRetry(() => import("./pages/surveys/Responses"));
const SurveyGroupVariablesIndex = lazyWithRetry(() => import("./pages/surveys/group-variables/Index"));
const SurveyAnalyticsResults = lazyWithRetry(() => import("./pages/surveys/analytics/Results"));
const SurveyAnalyticsResponse = lazyWithRetry(() => import("./pages/surveys/analytics/Response"));
const SurveyAnalyticsSummary = lazyWithRetry(() => import("./pages/surveys/analytics/Summary"));

// Email Templates
const EmailTemplatesIndex = lazyWithRetry(() => import("./pages/email-templates/Index"));
const EmailTemplatesEdit = lazyWithRetry(() => import("./pages/email-templates/Edit/Edit"));
const EmailTemplatesCreate = lazyWithRetry(() => import("./pages/email-templates/Create"));
const EmailGroupVariablesIndex = lazyWithRetry(() => import("./pages/email-templates/group-variables/Index"));

// Files
const FilesIndex = lazyWithRetry(() => import("./pages/files/Index"));

// Documents
const ReportsIndex = lazyWithRetry(() => import("./pages/reports/Index"));
const ReportEdit = lazyWithRetry(() => import("./pages/reports/ReportEdit"));
const DataSetsIndex = lazyWithRetry(() => import("./pages/datasets/Index"));
const DataSetEditor = lazyWithRetry(() => import("./pages/datasets/DataSetEditor"));

// Settings
const SettingsRedirect = lazyWithRetry(() => import("./pages/settings/components/SettingsRedirect"));
const Facilities = lazyWithRetry(() => import("./pages/settings/facilities/Facilities"));
const FacilityGroupsIndex = lazyWithRetry(() => import("./pages/settings/facility-groups/Index"));
const FacilityGroupsMappingValue = lazyWithRetry(() => import("./pages/settings/facility-groups/MappingValue"));
const UsersIndex = lazyWithRetry(() => import("./pages/settings/users/Index"));
const UsersEdit = lazyWithRetry(() => import("./pages/settings/users/Edit"));
const MappingKeysIndex = lazyWithRetry(() => import("./pages/settings/mapping-keys/Index"));
const TagsIndex = lazyWithRetry(() => import("./pages/settings/tags/Index"));
const RolesIndex = lazyWithRetry(() => import("./pages/settings/roles/Index"));
const GroupChildrenIndex = lazyWithRetry(() => import("./pages/settings/group-children/Index"));
const GroupChildrenTagsIndex = lazyWithRetry(() => import("./pages/settings/group-children/tags/Index"));

// Brandings (Charte graphique)
const BrandingsIndex = lazyWithRetry(() => import("./pages/settings/brandings/Index"));
const BrandingsUpdate = lazyWithRetry(() => import("./pages/settings/brandings/Update/Update"));

// Super-admin
const AnalyticsLayout = lazyWithRetry(() => import("./pages/super-admin/analytics/AnalyticsLayout"));
const AnalyticsGeneral = lazyWithRetry(() => import("./pages/super-admin/analytics/AnalyticsGeneral"));
const AnalyticsCampaigns = lazyWithRetry(() => import("./pages/super-admin/analytics/AnalyticsCampaigns"));
const CampaignStatsIndex = lazyWithRetry(() => import("./pages/campaign-stats/Index"));
const CampaignResponses = lazyWithRetry(() => import("./pages/campaigns/Responses"));
const CampaignEmails = lazyWithRetry(() => import("./pages/campaigns/Emails"));
const CampaignPlanning = lazyWithRetry(() => import("./pages/campaigns/Planning"));
const SAConfigRoles = lazyWithRetry(() => import("./pages/super-admin/config/roles/Index"));
const SAConfigCampaignRoles = lazyWithRetry(() => import("./pages/super-admin/config/campaign-roles/Index"));
const SAConfigMailTestMode = lazyWithRetry(() => import("./pages/super-admin/config/mail-test-mode/Index"));
const SAConfigMailbox = lazyWithRetry(() => import("./pages/super-admin/config/mailbox/Index"));
const SAConfigTwoFactor = lazyWithRetry(() => import("./pages/super-admin/config/two-factor/Index"));
const SAConfigEspace = lazyWithRetry(() => import("./pages/super-admin/config/espace/Index"));
const SARetentionIndex = lazyWithRetry(() => import("./pages/super-admin/retention/Index"));
const SAImportsIndex = lazyWithRetry(() => import("./pages/super-admin/imports/Index"));
const SATeamIndex = lazyWithRetry(() => import("./pages/super-admin/team/Index"));
const CampaignCategoriesIndex = lazyWithRetry(() => import("./pages/campaign-categories/Index"));
const CampaignCategoriesCreate = lazyWithRetry(() => import("./pages/campaign-categories/Create"));
const CampaignCategoriesEdit = lazyWithRetry(() => import("./pages/campaign-categories/Edit"));
const CampaignCategoriesFormTypes = lazyWithRetry(() => import("./pages/campaign-categories/FormTypes"));
const CampaignCategoriesFlows = lazyWithRetry(() => import("./pages/campaign-categories/Flows"));

// Espace hôtelier (public client)
const EspaceIndex = lazyWithRetry(() => import("./pages/espace/Index"));
const EspaceRequestCampaign = lazyWithRetry(() => import("./pages/espace/RequestCampaign"));
const EspaceCampaignReport = lazyWithRetry(() => import("./pages/espace/CampaignReport"));
const EspaceHistory = lazyWithRetry(() => import("./pages/espace/History"));

// Super-admin : demandes venues de l'espace hôtelier
const SACampaignRequests = lazyWithRetry(() => import("./pages/super-admin/campaign-requests/Index"));

// Facilities (Cardex)
const FacilitiesIndex = lazyWithRetry(() => import("./pages/facilities/Index"));
const FacilityDetail = lazyWithRetry(() => import("./pages/facilities/Detail"));

// ═══════════════════════════════════════════════════════
// Router
// ═══════════════════════════════════════════════════════
const router = createBrowserRouter([
    // Espace hôtelier : arbre séparé de celui du staff, avec son propre garde
    // et son propre layout. Aucune route d'administration n'y est atteignable,
    // et le serveur refuse de toute façon ce préfixe à un compte P&W.
    {
        path: '/espace',
        element: <RequireHotelier/>,
        loader: authLoader,
        children: [
            {
                element: <EspaceLayout/>,
                children: [
                    { path: '', element: <S><EspaceIndex /></S> },
                    { path: 'demande', element: <S><EspaceRequestCampaign /></S> },
                    // L'historique a sa propre adresse : sans elle, un hôtelier
                    // ayant une campagne en cours n'avait aucun chemin vers ses
                    // rapports passés. Constaté en vérification.
                    { path: 'campagnes', element: <S><EspaceHistory /></S> },
                    { path: 'campagnes/:id', element: <S><EspaceCampaignReport /></S> },
                ],
            },
        ],
    },

    // Routes authentifiées (staff)
    {
        path: '/',
        element: <RequireStaff/>,
        loader: authLoader,
        children: [
            // Enrôlement 2FA (hors layout principal : obligatoire pour les admins)
            { path: 'two-factor/setup', element: <S><TwoFactorSetup /></S> },
            {
                element: <DefaultLayout/>,
                children: [
                    {
                        element: <RequirePermission/>,
                        children: [
                            { path: '/', element: <SmartRedirect/> },

                            // Dashboard
                            { path: 'dashboard', element: <S><DashboardIndex /></S> },

                            // Statistiques de campagnes (utilisateurs de groupe P&W)
                            { path: 'campaign-stats', element: <S><CampaignStatsIndex /></S> },

                            // Compte utilisateur
                            { path: 'account/security', element: <S><AccountSecurity /></S> },

                            // Campaigns
                            {
                                path: 'campaigns',
                                children: [
                                    { path: '', element: <S><CampaignsIndex /></S> },
                                    { path: 'categories', element: <S><CampaignCategoriesIndex /></S> },
                                    { path: 'categories/create', element: <S><CampaignCategoriesCreate /></S> },
                                    { path: 'categories/:id/edit', element: <S><CampaignCategoriesEdit /></S> },
                                    { path: 'categories/:id/form-types', element: <S><CampaignCategoriesFormTypes /></S> },
                                    { path: 'categories/:id/flows', element: <S><CampaignCategoriesFlows /></S> },
                                    { path: ':id', element: <S><CampaignsShow /></S> },
                                    { path: 'planning', element: <S><CampaignPlanning /></S> },
                                    { path: ':id/responses', element: <S><CampaignResponses /></S> },
                                    { path: ':id/emails', element: <S><CampaignEmails /></S> },
                                    { path: ':id/alerts', element: <S><CampaignsAlerts /></S> },
                                    // L'atelier de rédaction : le rapport à gauche, ses zones à
                                    // droite. Hors du CampaignTabLayout — il occupe l'écran.
                                    { path: ':id/editorial/:reportId', element: <S><CampaignsWorkbench /></S> },
                                    { path: ':id/edit', element: <S><CampaignsEdit /></S> },
                                    { path: ':id/edit/flows', element: <S><CampaignsEditFlows /></S> },
                                ],
                            },

                            // Alerts
                            { path: 'alerts', element: <S><AlertsIndex /></S> },

                            // Facilities (Cardex)
                            { path: 'facilities', element: <S><FacilitiesIndex /></S> },
                            { path: 'facilities/:idFacility', element: <S><FacilityDetail /></S> },

                            // Flows
                            {
                                path: 'flows',
                                children: [
                                    { path: '', element: <S><FlowsIndex /></S> },
                                    { path: 'update/:idFlowStarter', element: <S><FlowsUpdate /></S> },
                                    {
                                        path: 'statistics/:idFlowStarter',
                                        children: [
                                            { path: 'overview', element: <S><FlowStatistics /></S> },
                                            { path: 'analyse', element: <S><FlowAnalyse /></S> },
                                            { path: 'flow', element: <S><FlowFlow /></S> },
                                            { path: 'logs', element: <S><FlowLogs /></S> },
                                            { path: 'heatmap', element: <S><FlowHeatmap /></S> },
                                        ],
                                    },
                                    { path: 'create', element: <S><FlowsCreate /></S> },
                                ],
                            },

                            // Surveys (admin)
                            {
                                path: 'surveys',
                                children: [
                                    { path: '', element: <Navigate to='/surveys/responses'/> },
                                    { path: 'list', element: <S><SurveysIndex /></S> },
                                    { path: 'responses', element: <S><SurveysResponses /></S> },
                                    { path: 'list/update/:idSurvey', element: <S><SurveysUpdate /></S> },
                                    {
                                        path: 'list/responses/:idSurvey',
                                        children: [
                                            { path: '', element: <S><SurveyAnalyticsResults /></S> },
                                            { path: ':surveyResponseId', element: <S><SurveyAnalyticsResponse /></S> },
                                            { path: 'summary', element: <S><SurveyAnalyticsSummary /></S> },
                                        ],
                                    },
                                    { path: 'list/create', element: <S><SurveysCreate /></S> },
                                    { path: 'group-variables', element: <S><SurveyGroupVariablesIndex /></S> },
                                ],
                            },

                            // Email Templates
                            {
                                path: 'email-templates',
                                children: [
                                    { path: '', element: <S><EmailTemplatesIndex /></S> },
                                    { path: ':idEmailTemplate', element: <S><EmailTemplatesEdit /></S> },
                                    { path: 'create', element: <S><EmailTemplatesCreate /></S> },
                                    { path: 'group-variables', element: <S><EmailGroupVariablesIndex /></S> },
                                ],
                            },

                            // Files
                            { path: 'files', children: [{ path: '', element: <S><FilesIndex /></S> }] },

                            // Documents
                            {
                                path: 'documents',
                                children: [
                                    { path: '', element: <Navigate to="/documents/reports" replace /> },
                                    { path: 'reports', element: <S><ReportsIndex /></S> },
                                    { path: 'reports/create', element: <S><ReportEdit /></S> },
                                    { path: 'reports/:idReport', element: <S><ReportEdit /></S> },
                                    { path: 'datasets', element: <S><DataSetsIndex /></S> },
                                    { path: 'datasets/create', element: <S><DataSetEditor /></S> },
                                    { path: 'datasets/edit/:id', element: <S><DataSetEditor /></S> },
                                ],
                            },

                            // Redirections anciennes URLs
                            { path: 'reports', element: <Navigate to="/documents/reports" replace /> },
                            { path: 'reports/*', element: <Navigate to="/documents/reports" replace /> },
                            { path: 'datasets', element: <Navigate to="/documents/datasets" replace /> },
                            { path: 'datasets/*', element: <Navigate to="/documents/datasets" replace /> },
                            { path: 'super-admin/facilities', element: <Navigate to="/facilities" replace /> },

                            // Settings
                            {
                                path: 'settings',
                                children: [
                                    { path: '', element: <S><SettingsRedirect /></S> },
                                    { path: 'facilities', element: <S><Facilities /></S> },
                                    { path: 'facilities/:idFacility', element: <S><Facilities /></S> },
                                    {
                                        path: 'facility-groups',
                                        children: [
                                            { path: '', element: <S><FacilityGroupsIndex /></S> },
                                            { path: ':facilityGroupId/mapping-values', element: <S><FacilityGroupsMappingValue /></S> },
                                        ],
                                    },
                                    {
                                        path: 'users',
                                        children: [
                                            { path: '', element: <S><UsersIndex /></S> },
                                            { path: ':idUser', element: <S><UsersEdit /></S> },
                                        ],
                                    },
                                    {
                                        path: 'roles',
                                        children: [
                                            { path: '', element: <S><RolesIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'tags',
                                        children: [
                                            { path: '', element: <S><TagsIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'mapping-keys',
                                        children: [
                                            { path: '', element: <S><MappingKeysIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'group-children',
                                        children: [
                                            { path: '', element: <S><GroupChildrenIndex /></S> },
                                            { path: 'tags', element: <S><GroupChildrenTagsIndex /></S> },
                                        ],
                                    },
                                    // Brandings (Charte graphique)
                                    { path: 'brandings', element: <S><BrandingsIndex /></S> },
                                    { path: 'brandings/:idBranding', element: <S><BrandingsUpdate /></S> },
                                ],
                            },

                            // Super-admin
                            {
                                path: 'super-admin',
                                children: [
                                    {
                                        path: 'facilities',
                                        children: [
                                            // L'ancienne liste super-admin est fusionnée dans /facilities
                                            { path: '', element: <Navigate to="/facilities" replace /> },
                                        ],
                                    },
                                    {
                                        path: 'analytics',
                                        element: <S><AnalyticsLayout /></S>,
                                        children: [
                                            { index: true, element: <S><AnalyticsGeneral /></S> },
                                            { path: 'campaigns', element: <S><AnalyticsCampaigns /></S> },
                                        ],
                                    },
                                    {
                                        path: 'config',
                                        children: [
                                            { path: '', element: <Navigate to="roles" replace /> },
                                            { path: 'roles', element: <S><SAConfigRoles /></S> },
                                            { path: 'campaign-roles', element: <S><SAConfigCampaignRoles /></S> },
                                            { path: 'mail-test-mode', element: <S><SAConfigMailTestMode /></S> },
                                            { path: 'mailbox', element: <S><SAConfigMailbox /></S> },
                                            { path: 'two-factor', element: <S><SAConfigTwoFactor /></S> },
                                            { path: 'espace', element: <S><SAConfigEspace /></S> },
                                        ],
                                    },
                                    {
                                        path: 'campaign-requests',
                                        children: [
                                            { path: '', element: <S><SACampaignRequests /></S> },
                                        ],
                                    },
                                    {
                                        path: 'retention',
                                        children: [
                                            { path: '', element: <S><SARetentionIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'team',
                                        children: [
                                            { path: '', element: <S><SATeamIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'imports',
                                        children: [
                                            { path: '', element: <S><SAImportsIndex /></S> },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },

    // Routes Auth (guest)
    {
        path: '/',
        element: <GuestLayout/>,
        children: [
            { path: 'login', element: <S><Login /></S> },
            { path: 'login/:slug', element: <S><Login /></S> },
            { path: 'signup', element: <S><Signup /></S> },
            { path: 'signup/:slug', element: <S><Signup /></S> },
            { path: 'forgot-password', element: <S><ForgotPassword /></S> },
            { path: 'forgot-password/:slug', element: <S><ForgotPassword /></S> },
            { path: 'reset-password/:token', element: <S><ResetPassword /></S> },
            { path: 'reset-password/:slug/:token', element: <S><ResetPassword /></S> },
        ],
    },

    // Routes publiques
    {
        path: '/',
        element: <PublicLayout/>,
        children: [
            {
                path: 'survey-response',
                children: [
                    // Lien de questionnaire de campagne : la Réponse n'est rattachée
                    // qu'à la campagne, jamais à l'envoi qui a porté le lien.
                    { path: ':surveySlug/c/:campaignSlug', element: <S><SurveyResponses /></S> },
                    { path: ':surveySlug', element: <S><SurveyResponses /></S> },
                    { path: ':surveySlug/:actionLaunchReservationSlug', element: <S><SurveyResponses /></S> },
                ],
            },
            // Invitation d'un hôtelier. Sous `PublicLayout` et non `GuestLayout` :
            // ce dernier renvoie vers l'accueil dès qu'une session existe, et le
            // destinataire d'un lien reçu par courrier peut très bien partager
            // son navigateur avec un collègue déjà connecté. Constaté en
            // vérification : le lien renvoyait vers /facilities sans un mot.
            { path: 'invitation/:token', element: <S><AcceptInvitation /></S> },
            { path: 'public-document/:documentSlug/:trackingSlug', element: <S><PublicDocument /></S> },
            { path: 'report-export/:slug', element: <S><ReportExportView /></S> },
        ],
    },

    // 404
    {
        path: '*',
        element: <NotFound/>
    },
]);

export default router;
